import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CustomerDetailModal } from '../../components/dem/CustomerDetailModal';
import { CustomerListRow } from '../../components/dem/CustomerListRow';
import { DeliveryScheduleIssuesModal } from '../../components/dem/DeliveryScheduleIssuesModal';
import { DemCustomerFilters, type DemStatusFilter } from '../../components/dem/DemCustomerFilters';
import { PageToolbar } from '../../components/ui/PageToolbar';
import { useDemMasterDataRealtime } from '../../hooks/useDemMasterDataRealtime';
import {
  fetchDemCustomer,
  fetchDemCustomers,
  fetchActiveCustomersWithScheduleIssues,
  autoGenerateMissingPortalTokens,
  countCustomersMissingPortalToken,
  type DeliveryScheduleIssueRow,
  type DemCustomerSort,
} from '../../lib/demCustomers';
import type { DemCustomer } from '../../types/dem';

const PAGE_SIZE = 50;
const DEFAULT_SORT: DemCustomerSort = 'code_asc';

type CustomerSortColumn = 'code' | 'name';

const SORT_COLUMNS: { key: CustomerSortColumn; label: string }[] = [
  { key: 'code', label: 'Code' },
  { key: 'name', label: 'Name' },
];

function sortColumnOf(sort: DemCustomerSort): CustomerSortColumn {
  return sort.startsWith('name') ? 'name' : 'code';
}

function sortDirOf(sort: DemCustomerSort): 'asc' | 'desc' {
  return sort.endsWith('_asc') ? 'asc' : 'desc';
}

export function DemCustomersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState<DemCustomerSort>(DEFAULT_SORT);
  const [blockedFilter, setBlockedFilter] = useState<DemStatusFilter>('all');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<DemCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<DemCustomer | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [uidBusy, setUidBusy] = useState(false);
  const [uidProgress, setUidProgress] = useState<string | null>(null);
  const [uidMessage, setUidMessage] = useState<string | null>(null);
  const [scheduleIssuesOpen, setScheduleIssuesOpen] = useState(false);
  const [scheduleIssueRows, setScheduleIssueRows] = useState<DeliveryScheduleIssueRow[]>([]);
  const [scheduleIssueCount, setScheduleIssueCount] = useState<number | null>(null);
  const [scheduleIssuesLoading, setScheduleIssuesLoading] = useState(false);
  const [scheduleIssuesError, setScheduleIssuesError] = useState<string | null>(null);

  const activeSortCol = sortColumnOf(sort);
  const activeSortDir = sortDirOf(sort);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sort, blockedFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchDemCustomers({
        search: debouncedSearch,
        page,
        pageSize: PAGE_SIZE,
        sort,
        blockedFilter,
        statusFilter: 'active',
      });
      setRows(result.rows);
      setTotal(result.total);
      setSelected((prev) => {
        if (!prev) return null;
        return result.rows.find((row) => row.code === prev.code) ?? prev;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load customers.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, sort, blockedFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useDemMasterDataRealtime({
    include: ['customers'],
    onChange: () => {
      void load();
    },
  });

  const refreshScheduleIssueCount = useCallback(async () => {
    try {
      const issueRows = await fetchActiveCustomersWithScheduleIssues();
      setScheduleIssueCount(issueRows.length);
    } catch {
      // Count is advisory — keep last known value on background refresh failure.
    }
  }, []);

  useEffect(() => {
    void refreshScheduleIssueCount();
  }, [refreshScheduleIssueCount]);

  const openScheduleIssues = async () => {
    setScheduleIssuesOpen(true);
    setScheduleIssuesLoading(true);
    setScheduleIssuesError(null);
    try {
      const issueRows = await fetchActiveCustomersWithScheduleIssues();
      setScheduleIssueRows(issueRows);
      setScheduleIssueCount(issueRows.length);
    } catch (e) {
      setScheduleIssuesError(
        e instanceof Error ? e.message : 'Failed to load schedule issues.',
      );
      setScheduleIssueRows([]);
    } finally {
      setScheduleIssuesLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const from = total === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const to = Math.min(safePage * PAGE_SIZE, total);

  function toggleSort(column: CustomerSortColumn) {
    if (activeSortCol === column) {
      setSort(`${column}_${activeSortDir === 'asc' ? 'desc' : 'asc'}` as DemCustomerSort);
      return;
    }
    setSort(`${column}_asc` as DemCustomerSort);
  }

  // Deep-link from CloudTalk: /dem/customers?code=...
  useEffect(() => {
    const code = searchParams.get('code')?.trim();
    if (!code) return;
    let cancelled = false;
    void (async () => {
      try {
        const customer = await fetchDemCustomer(code);
        if (cancelled || !customer) return;
        setSearch(code);
        setSelected(customer);
        setModalOpen(true);
        const next = new URLSearchParams(searchParams);
        next.delete('code');
        setSearchParams(next, { replace: true });
      } catch {
        /* ignore deep-link miss */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openCustomer = (customer: DemCustomer) => {
    setSelected(customer);
    setModalOpen(true);
  };

  const handleAutoGenerateUids = async () => {
    setUidMessage(null);
    try {
      const missing = await countCustomersMissingPortalToken();
      if (missing === 0) {
        setUidMessage('All customers already have a Portal UID.');
        return;
      }

      const ok = window.confirm(
        `Found ${missing.toLocaleString()} customer${missing === 1 ? '' : 's'} without a Portal Link.\n\nProceed to generate?`,
      );
      if (!ok) return;

      setUidBusy(true);
      setUidProgress('Preparing…');
      const updated = await autoGenerateMissingPortalTokens((current, totalCount) => {
        setUidProgress(`Generating… ${current} / ${totalCount}`);
      });
      setUidMessage(
        updated > 0
          ? `Successfully generated ${updated.toLocaleString()} new UID${updated === 1 ? '' : 's'}.`
          : 'No UIDs were saved. Your account may not have write permission on customers yet.',
      );
      await load();
    } catch (e) {
      setUidMessage(e instanceof Error ? e.message : 'Failed to generate UIDs.');
    } finally {
      setUidBusy(false);
      setUidProgress(null);
    }
  };

  const emptyMessage = 'No customers match your search and filters.';

  return (
    <div className="dem-customers-page dem-customers-page--floating">
      <div className="dem-floating-page-actions dem-floating-page-actions--end">
        <div className="dem-floating-page-actions__btns">
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => void load()}
            disabled={loading || uidBusy}
          >
            Refresh
          </button>
          <button
            type="button"
            className={`btn-secondary btn-sm dem-btn-schedule-issues${
              scheduleIssueCount && scheduleIssueCount > 0
                ? ' dem-btn-schedule-issues--warn'
                : ''
            }`}
            disabled={uidBusy || loading || scheduleIssuesLoading}
            onClick={() => void openScheduleIssues()}
          >
            Schedule issues
            {scheduleIssueCount != null && scheduleIssueCount > 0
              ? ` (${scheduleIssueCount.toLocaleString()})`
              : ''}
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm dem-btn-generate-uid"
            disabled={uidBusy || loading}
            onClick={() => void handleAutoGenerateUids()}
          >
            {uidBusy ? 'Generating UIDs…' : 'Auto Generate UIDs'}
          </button>
        </div>
      </div>

      <PageToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by code or name"
        leading={
          <div className="dem-customers-page__toolbar-aside dem-customers-page__toolbar-aside--leading">
            <DemCustomerFilters value={blockedFilter} onChange={setBlockedFilter} />
            <span className="page-count">
              {loading
                ? 'Loading…'
                : total === 0
                  ? '0 customers'
                  : `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()} customer${total === 1 ? '' : 's'}`}
            </span>
          </div>
        }
      />

      {(uidProgress || uidMessage) && (
        <p
          className={`dem-uid-status${uidMessage?.includes('Successfully') ? ' dem-uid-status--ok' : ''}`}
          role="status"
        >
          {uidProgress ?? uidMessage}
        </p>
      )}

      {error && <p className="alert alert-error">{error}</p>}

      {loading && rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__spinner" />
          <p>Loading customers…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p>{emptyMessage}</p>
        </div>
      ) : (
        <div className="card dem-customer-list">
          <div className="dem-customer-list__head" role="row">
            {SORT_COLUMNS.map(({ key, label }) => {
              const active = activeSortCol === key;
              const ariaSort = active ? (activeSortDir === 'asc' ? 'ascending' : 'descending') : 'none';
              return (
                <button
                  key={key}
                  type="button"
                  className={`dem-customer-list__sort-btn${active ? ' is-active' : ''}`}
                  onClick={() => toggleSort(key)}
                  aria-sort={ariaSort}
                >
                  <span>{label}</span>
                  {active ? (
                    <span className="dem-customer-list__sort-ind" aria-hidden>
                      {activeSortDir === 'asc' ? '▲' : '▼'}
                    </span>
                  ) : null}
                </button>
              );
            })}
            <span>Email</span>
            <span>MOQ</span>
            <span>Delivery</span>
            <span>Portal UID</span>
          </div>
          {rows.map((customer) => (
            <CustomerListRow
              key={customer.code}
              customer={customer}
              selected={modalOpen && selected?.code === customer.code}
              onOpen={openCustomer}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="dem-pagination">
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={safePage <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="dem-pagination__label">
            Page {safePage} of {totalPages}
          </span>
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={safePage >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      )}

      <DeliveryScheduleIssuesModal
        open={scheduleIssuesOpen}
        onClose={() => setScheduleIssuesOpen(false)}
        rows={scheduleIssueRows}
        loading={scheduleIssuesLoading}
        error={scheduleIssuesError}
        onOpenCustomer={(customer) => {
          setScheduleIssuesOpen(false);
          openCustomer(customer);
        }}
      />

      <CustomerDetailModal
        customer={selected}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={(updated) => {
          setRows((prev) => prev.map((r) => (r.code === updated.code ? updated : r)));
          setSelected(updated);
          void refreshScheduleIssueCount();
        }}
      />
    </div>
  );
}
